/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { CodeShareUser, User } from "types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../reduxStore";

export interface AuthState {
  user: User | undefined;
  accessToken: string | undefined;
  expires: number | undefined;

  // During regular sign-in silent way is tried first. It takes some time, so this is to indicate it's in progress
  // Automatic silent sign-in does not change this state
  silentSigninInProgress?: boolean;

  // If automatic silent sign-in fails, error state is recorded to prevent redundant calls to silent sign-in
  silentSigninError?: boolean;

  sandboxUser?: CodeShareUser,
};

const initialState: AuthState = {
  user: undefined,
  accessToken: undefined,
  expires: undefined,
  silentSigninInProgress: false,
  silentSigninError: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Set valid credentials to store after succesfull authentication
    setCredentials: (state, action: PayloadAction<AuthState>) => {
      if (!state.user)
        state.user = action.payload.user;
      else {
        Object.assign(state.user, action.payload.user);
      }

      state.accessToken = action.payload.accessToken;
      state.expires = action.payload.expires;
      state.silentSigninInProgress = false;
      state.silentSigninError = false;
    },
    // remove credential from store if access token cannot bet renewed automatically
    removeCredentials: (state) => {
      state.user = undefined;
      state.accessToken = undefined;
      state.expires = undefined;
      // leaving silentSigninError unchanged, to prevent redundat retries to sign-in
    },
    // indicates silent sign-in attempt going on. Silent signin is always tried first, if it fails then interactive sign-in is initiated
    setSilentSigninStatus: (state, action: PayloadAction<boolean>) => {
      state.silentSigninInProgress = action.payload;
    },
    setSilentSigninError: (state, action: PayloadAction<boolean>) => {
      state.silentSigninError = action.payload;
    },
    sandboxUserLoaded: (state, { payload }: PayloadAction<CodeShareUser>) => {
      state.sandboxUser = payload;
    }
  },
});

export const {
  setCredentials,
  removeCredentials,
  setSilentSigninStatus,
  setSilentSigninError,
  sandboxUserLoaded,
} = authSlice.actions;

export const selectCurrentUser = (state: RootState) => state.auth.user;

export default authSlice.reducer;
